import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Newspaper, Plus, Pencil, Trash2, Loader2, ExternalLink, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { title: '', slug: '', excerpt: '', meta_description: '', tags: '', content_md: '', sources: '', status: 'draft' };

export const BlogAdminPanel = () => {
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [theme, setTheme] = useState('');
  const [themes, setThemes] = useState([]);

  const load = () => axios.get(`${API}/admin/blog`, { withCredentials: true }).then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); axios.get(`${API}/admin/blog/themes`, { withCredentials: true }).then((r) => setThemes(r.data.themes)).catch(() => {}); }, []);

  const edit = async (p) => {
    const { data } = await axios.get(`${API}/admin/blog/${p.id}`, { withCredentials: true });
    setForm({ ...data, tags: (data.tags || []).join(', '), sources: (data.sources || []).map((s) => `${s.name} | ${s.url}`).join('\n') });
  };

  const save = async () => {
    setSaving(true);
    const payload = {
      ...form,
      tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      sources: form.sources.split('\n').map((l) => l.split('|')).filter((p) => p.length === 2).map(([name, url]) => ({ name: name.trim(), url: url.trim() })),
    };
    try {
      if (form.id) await axios.put(`${API}/admin/blog/${form.id}`, payload, { withCredentials: true });
      else await axios.post(`${API}/admin/blog`, payload, { withCredentials: true });
      toast.success('Article enregistré');
      setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } finally { setSaving(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer « ${p.title} » ?`)) return;
    await axios.delete(`${API}/admin/blog/${p.id}`, { withCredentials: true });
    toast.success('Article supprimé'); load();
  };

  const generate = async (kind) => {
    setGenerating(kind);
    try {
      const { data } = await axios.post(`${API}/admin/blog/generate`, { kind, theme: kind === 'weekly' ? theme || undefined : undefined }, { withCredentials: true });
      toast.success(`Brouillon IA créé : ${data.title}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Génération impossible'); } finally { setGenerating(null); }
  };

  const f = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Card data-testid="blog-admin-panel">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="font-heading text-lg flex items-center gap-2"><Newspaper className="w-5 h-5 text-[#1E3A8A]" /> Blog ({posts.length} article{posts.length > 1 ? 's' : ''}, {posts.filter((p) => p.status === 'draft').length} à relire)</CardTitle>
        <Button size="sm" onClick={() => setForm(EMPTY)} data-testid="blog-new-btn"><Plus className="w-4 h-4 mr-1" /> Nouvel article</Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg border border-dashed border-[#1E3A8A]/30 bg-[#F9FAFB] p-4 space-y-2" data-testid="blog-ai-panel">
          <p className="text-sm font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#F59E0B]" /> Rédaction IA (Gemini) — un brouillon par semaine sur l'actualité du bâtiment + « Le point BTP du mois » le 1er. Vous validez avant publication.</p>
          <div className="flex flex-wrap items-center gap-2">
            <Input list="blog-themes" value={theme} onChange={(e) => setTheme(e.target.value)} placeholder="Thème (optionnel) — ex. nouveaux matériaux biosourcés" className="input-field h-9 flex-1 min-w-[240px]" data-testid="blog-ai-theme" />
            <datalist id="blog-themes">{themes.map((t) => <option key={t} value={t} />)}</datalist>
            <Button size="sm" variant="outline" onClick={() => generate('weekly')} disabled={!!generating} data-testid="blog-generate-weekly">{generating === 'weekly' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Générer un article'}</Button>
            <Button size="sm" variant="outline" onClick={() => generate('monthly')} disabled={!!generating} data-testid="blog-generate-monthly">{generating === 'monthly' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Point BTP du mois'}</Button>
          </div>
        </div>
        {form && (
          <div className="border rounded-lg p-4 bg-[#F9FAFB] space-y-3" data-testid="blog-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Titre</Label><Input value={form.title} onChange={f('title')} className="input-field" data-testid="blog-title-input" /></div>
              <div><Label>Slug (URL)</Label><Input value={form.slug} onChange={f('slug')} placeholder="généré depuis le titre" className="input-field" /></div>
            </div>
            <div><Label>Extrait (chapeau)</Label><Textarea rows={2} value={form.excerpt} onChange={f('excerpt')} className="input-field" /></div>
            <div><Label>Meta description ({(form.meta_description || '').length}/160)</Label><Input value={form.meta_description} onChange={f('meta_description')} maxLength={160} className="input-field" /></div>
            <div><Label>Tags (séparés par des virgules)</Label><Input value={form.tags} onChange={f('tags')} className="input-field" /></div>
            <div><Label>Contenu (Markdown — commencez par une réponse directe, puis titres ## et listes)</Label><Textarea rows={14} value={form.content_md} onChange={f('content_md')} className="input-field font-mono text-sm" data-testid="blog-content-input" /></div>
            <div><Label>Sources (une par ligne : Nom | https://…)</Label><Textarea rows={3} value={form.sources} onChange={f('sources')} className="input-field text-sm" /></div>
            <div className="flex flex-wrap items-center gap-3">
              <select value={form.status} onChange={f('status')} className="input-field h-9 w-40" data-testid="blog-status-select"><option value="draft">Brouillon</option><option value="published">Publié</option></select>
              <Button size="sm" onClick={save} disabled={saving} data-testid="blog-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}</Button>
              <Button size="sm" variant="ghost" onClick={() => setForm(null)}>Annuler</Button>
            </div>
          </div>
        )}
        <div className="divide-y">
          {posts.map((p) => (
            <div key={p.id} className="py-3 flex flex-wrap items-center justify-between gap-2" data-testid={`blog-admin-row-${p.slug}`}>
              <div className="min-w-0">
                <div className="font-medium text-sm flex items-center gap-2">{p.title}<Badge className={`text-[10px] ${p.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>{p.status === 'published' ? 'Publié' : 'Brouillon'}</Badge>{p.ai_generated && <Badge className="text-[10px] bg-amber-100 text-amber-700">IA — à relire</Badge>}</div>
                <p className="text-xs text-[#9CA3AF]">/blog/{p.slug} · modifié le {new Date(p.updated_at).toLocaleDateString('fr-BE')}</p>
              </div>
              <div className="flex gap-1">
                {p.status === 'published' && <a href={`/blog/${p.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-[#6B7280] hover:text-[#1E3A8A]"><ExternalLink className="w-4 h-4" /></a>}
                <Button size="sm" variant="ghost" onClick={() => edit(p)} data-testid={`blog-edit-${p.slug}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => remove(p)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
